import React, { FC, ReactNode, useState, useEffect } from 'react';
import BootstrapCollapse from 'react-bootstrap/Collapse';
import { kebabCase } from 'lodash';
import { FAIcon } from '../fa-icon';
import { FieldGroup } from '../Form/FieldGroup';
import { CardTitle, CardActionBox, CardMediaBox } from '../CardComponents';
import { AttentionGrabber } from '../attention-grabber';
import { formatClassName, pickDataAttributes } from '../../utils';
import { DataAttributes } from '../../utils/pickDataAttributes';
import { ConstructionCardProps } from './types';
import { Card, CardHeader, CardBody } from '../../core/components/Card';
import { Layout as CoreLayout } from '../../core';
import { Box, Center, Collapse, Flex, Image, Spacer } from '@chakra-ui/react';

export const ConstructionCard: FC<ConstructionCardProps> = ({
  alwaysCollapsed = false,
  alwaysExpanded = false,
  attentionGrabber,
  buttonConfig = [],
  cardContentClasses = '',
  chakra = 'on',
  chevronPosition = 'right',
  children,
  className = '',
  customActionBar,
  data,
  formWrapper = false,
  hasMedia = false,
  headerImageSize = 'small',
  headerImageUrl,
  onExpandedToggle,
  overrideOverflowY,
  size = 'full',
  styleConfig = { background: 'white', shadow: 'none' },
  title,
  titleCentered = false,
  titleIcon,
  titleSize = 16,
  ...props
}) => {
  const [expanded, setExpanded] = useState<boolean>(!!props.expanded);
  const [showShadow, setShowShadow] = useState<boolean>(styleConfig?.shadow === 'light');

  const dataAttributes: DataAttributes = pickDataAttributes(props, 'construction-card') as DataAttributes;
  if (!dataAttributes['data-action']) {
    dataAttributes['data-action'] = title ? `open-${kebabCase(title)}-card` : 'open-contstruction-card';
  }

  const showChevron: boolean = !alwaysExpanded && !alwaysCollapsed;
  const chakraDisabled: boolean = chakra === 'off';

  useEffect(() => {
    setExpanded(!!props.expanded);
  }, [props.expanded]);

  useEffect(() => {
    if (typeof onExpandedToggle === 'function') {
      onExpandedToggle(expanded);
    }
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setShowShadow(styleConfig?.shadow === 'light');
  }, [styleConfig]);

  const getCardClasses = () =>
    formatClassName([
      'construction-card',
      `construction-card-${size}`,
      `with-title-${titleSize}`,
      `card-background-${styleConfig.background}`,
      `card-shadow-${showShadow ? 'light' : 'none'}`,
      { 'card-expanded': alwaysExpanded || expanded },
      { 'fixed-height': !!styleConfig.fixedHeight },
      { 'title-centered': titleCentered },
      { 'centered-image': !!headerImageUrl },
      className,
    ]);

  const renderActions = () => {
    if (!buttonConfig && !customActionBar) {
      return null;
    }

    return (
      customActionBar || (
        <CardActionBox
          chakra={chakra}
          buttons={buttonConfig}
          className="card-actions"
          onClick={event => event.stopPropagation()}
        />
      )
    );
  };

  const onMouseEnter = (): void => {
    if (styleConfig?.shadow === 'hover') setShowShadow(true);
  };

  const onMouseLeave = (): void => {
    if (styleConfig?.shadow === 'hover') setShowShadow(false);
  };

  const renderCardHeader = () => {
    return (
      <Box onClick={showChevron ? () => setExpanded(!expanded) : undefined}>
        {headerImageUrl && (
          <Center>
            <Image src={headerImageUrl} alt="Card Image" boxSize="110px" m="2" />
          </Center>
        )}

        <Flex alignContent={'flex-start'}>
          {title && (
            <CardTitle
              size={titleSize}
              icon={titleIcon}
              titleDataAttributes={{ optionalAttributes: { action: 'card-title' } }}
            >
              {title}

              {showChevron && <FAIcon icon={expanded ? 'angle-down' : 'angle-right'} />}
            </CardTitle>
          )}

          {attentionGrabber && <AttentionGrabber {...attentionGrabber} />}

          <Spacer />

          {!titleCentered && renderActions()}
        </Flex>
      </Box>
    );
  };

  const renderContentWrapper = (children: ReactNode) => {
    const classes = formatClassName([
      'construction-card-content',
      { 'card-expanded': !chakraDisabled && (alwaysExpanded || expanded) },
      cardContentClasses,
    ]);

    return formWrapper ? (
      <FieldGroup {...props} data={data} className={classes}>
        {children}
      </FieldGroup>
    ) : (
      <div className={classes}>{children}</div>
    );
  };

  const renderCardContent = () => {
    if (alwaysCollapsed) {
      return null;
    }

    const content = renderContentWrapper(hasMedia ? <div className="media-container">{children}</div> : children);

    return alwaysExpanded || !chakraDisabled ? content : <BootstrapCollapse in={expanded}>{content}</BootstrapCollapse>;
  };

  if (chakraDisabled) {
    return (
      <div
        {...dataAttributes}
        className={getCardClasses()}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{ maxHeight: styleConfig.fixedHeight }}
      >
        <div
          className="card-header"
          onClick={showChevron ? () => setExpanded(!expanded) : undefined}
          role={showChevron ? 'button' : undefined}
        >
          {headerImageUrl && (
            <div className="media-container">
              <CardMediaBox chakra="off" mediaUrl={headerImageUrl} mediaSize={headerImageSize} mediaPosition="center" />
            </div>
          )}

          <div className={formatClassName(['main', `with-chevron-${chevronPosition}`])}>
            {title && (
              <CardTitle
                chakra="off"
                size={titleSize}
                icon={titleIcon}
                titleDataAttributes={{ optionalAttributes: { action: 'card-title' } }}
              >
                {title}
              </CardTitle>
            )}

            {showChevron && (
              <FAIcon
                icon={expanded ? 'angle-down' : 'angle-right'}
                onClick={() => setExpanded(!expanded)}
                additionalClasses="card-chevron"
              />
            )}

            {attentionGrabber && <AttentionGrabber {...attentionGrabber} />}
          </div>

          {!titleCentered && renderActions()}
        </div>

        {renderCardContent()}

        {titleCentered && renderActions()}
      </div>
    );
  } else {
    const isExpanded = expanded || alwaysExpanded;
    return (
      <Card
        dataObjectType={dataAttributes['data-object-type'] as string}
        dataAction={dataAttributes['data-action'] as string}
        overflowY={overrideOverflowY || 'auto'}
        className={className}
      >
        <CardHeader pb={isExpanded ? undefined : 5}>
          {titleCentered || headerImageUrl ? <Center>{renderCardHeader()}</Center> : renderCardHeader()}
        </CardHeader>
        <Collapse startingHeight=".75lh" in={isExpanded} style={{ overflow: isExpanded ? 'initial' : 'hidden' }}>
          <CardBody>
            <CoreLayout width="100%">
              {renderCardContent()}
              {titleCentered && renderActions()}
            </CoreLayout>
          </CardBody>
        </Collapse>
      </Card>
    );
  }
};
